import { ChevronDown, Download, Eye, Loader2, Pencil } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { cn } from '@/lib/utils';
import type { Quote } from '@/types';
import { ownerInitials, quoteValueLabel, quotesMaxValueCents, relativeDays } from './pipelineFormat';
import { QuoteStatusBadge } from './QuoteBadges';
import {
  canViewQuote,
  getQuoteDealLabel,
  getQuoteDealUrl,
  getQuoteOwnerLabel,
  getViewButtonTitle,
  getViewLabel,
} from './quoteMeta';
import { QuoteSignalBadges } from './QuoteSignalBadges';
import { QuoteSignatureStatus } from './QuoteSignatureStatus';
import { formatSignedDate, getQuoteSignedAt, hasSignatureStatus } from './signatureStatus';
import { QuoteRowActions, type QuoteActionHandlers, type QuoteBusyState } from './QuoteRowActions';
import { describeExpiry } from './quoteSignals';
import { QuoteTotalsFooter } from './QuoteTotalsFooter';

/** Vaste kolombreedtes: alleen zo blijven icoon- en actiekolommen over alle rijen uitlijnen. */
const HEAD_CLASS = 'px-3 py-0 text-left text-[11px] font-semibold uppercase tracking-[0.08em] text-muted-foreground';

export function QuoteTable({
  quotes,
  totalCount,
  portalId,
  selectionMode,
  selectedIds,
  onToggleSelect,
  onToggleSelectAll,
  allSelected,
  someSelected,
  actionsFor,
  busy,
  sortColumn,
}: {
  quotes: Quote[];
  totalCount: number;
  portalId?: string;
  selectionMode: boolean;
  selectedIds: Set<string>;
  onToggleSelect: (id: string) => void;
  onToggleSelectAll: () => void;
  allSelected: boolean;
  someSelected: boolean;
  actionsFor: (quote: Quote) => QuoteActionHandlers;
  busy: QuoteBusyState;
  /** Kolom waarop nu gesorteerd wordt, zodat de kop dat laat zien. */
  sortColumn: 'updated' | 'created' | 'title' | null;
}) {
  const now = Date.now();
  const totalCents = quotes.reduce((sum, quote) => sum + (quote.valueCents ?? 0), 0);
  const totalMaxCents = quotesMaxValueCents(quotes);
  /**
   * Na ondertekening zeggen verloop- en wijzigdatum niets meer; dan neemt de
   * tekendatum beide kolommen over. Is alles in beeld getekend (zoals onder
   * Geaccepteerd), dan zegt de kop dat ook.
   */
  const signedAtById = new Map(quotes.map((quote) => [quote.id, getQuoteSignedAt(quote)]));
  const allSigned = quotes.length > 0 && quotes.every((quote) => signedAtById.get(quote.id));

  return (
    <div className="overflow-hidden rounded-md border bg-card">
      <div className="max-w-full overflow-x-auto">
        {/* Breedte volgt de vaste kolommen plus ruimte voor de offertetitel:
            zonder ondergrens knijpt de titelkolom zich dicht. */}
        <table className="w-full min-w-[1392px] table-fixed text-sm">
          <thead>
            <tr className="h-10 border-b bg-muted/60">
              {selectionMode && (
                <th className="w-10 px-3">
                  <Checkbox
                    checked={allSelected ? true : someSelected ? 'indeterminate' : false}
                    onCheckedChange={onToggleSelectAll}
                    aria-label="Alles selecteren"
                  />
                </th>
              )}
              <th className={cn(HEAD_CLASS, 'pl-4')}>
                <span className="inline-flex items-center gap-1">
                  Offerte
                  {sortColumn === 'title' && <ChevronDown className="h-3 w-3 shrink-0" />}
                </span>
              </th>
              <th className={cn(HEAD_CLASS, 'w-[150px]')}>Deal</th>
              <th className={cn(HEAD_CLASS, 'w-[132px]')}>Bedrijf</th>
              <th className={cn(HEAD_CLASS, 'w-[128px]')}>Eigenaar</th>
              <th className={cn(HEAD_CLASS, 'w-[172px]')}>Status</th>
              <th className={cn(HEAD_CLASS, 'w-[150px]')} title="Wie heeft er al ondertekend?">
                Ondertekening
              </th>
              <th className={cn(HEAD_CLASS, 'w-[104px] text-right')}>Waarde</th>
              {allSigned ? (
                <th colSpan={2} className={cn(HEAD_CLASS, 'w-[216px]')}>
                  Getekend
                </th>
              ) : (
                <>
                  <th className={cn(HEAD_CLASS, 'w-[104px]')}>Verloopt</th>
                  <th className={cn(HEAD_CLASS, 'w-[112px]')}>
                    <span className="inline-flex items-center gap-1">
                      {sortColumn === 'created' ? 'Aangemaakt' : 'Gewijzigd'}
                      {(sortColumn === 'updated' || sortColumn === 'created') && (
                        <ChevronDown className="h-3 w-3 shrink-0 text-foreground" />
                      )}
                    </span>
                  </th>
                </>
              )}
              <th className={cn(HEAD_CLASS, 'w-[132px] pr-4 text-right')}>Acties</th>
            </tr>
          </thead>
          <tbody>
            {quotes.map((quote) => {
              const actions = actionsFor(quote);
              const dealLabel = getQuoteDealLabel(quote);
              const dealUrl = getQuoteDealUrl(quote, portalId);
              const ownerLabel = getQuoteOwnerLabel(quote);
              const viewLabel = getViewLabel(quote);
              const expiry = describeExpiry(quote, now);
              const showSignatureStatus = hasSignatureStatus(quote);
              const selected = selectedIds.has(quote.id);
              const isPdfDownloading = busy.pdfDownloadBusy === quote.id;
              const signedAt = signedAtById.get(quote.id);
              const subtitle = [viewLabel, signedAt ? null : `gewijzigd ${relativeDays(quote.updatedAt, now)}`]
                .filter(Boolean)
                .join(' · ');

              return (
                <tr
                  key={quote.id}
                  className={cn(
                    'border-b transition-colors last:border-0 hover:bg-muted/30',
                    selected && 'bg-primary/5',
                  )}
                  onClick={selectionMode ? () => onToggleSelect(quote.id) : undefined}
                >
                  {selectionMode && (
                    <td className="w-10 px-3 py-3">
                      <Checkbox
                        checked={selected}
                        onCheckedChange={() => onToggleSelect(quote.id)}
                        onClick={(e) => e.stopPropagation()}
                        aria-label={`${quote.title} selecteren`}
                      />
                    </td>
                  )}
                  <td className="min-w-0 py-3 pl-4 pr-3">
                    <button
                      type="button"
                      onClick={(e) => {
                        e.stopPropagation();
                        actions.onEdit();
                      }}
                      title={quote.title}
                      className="block max-w-full truncate text-left font-semibold text-foreground hover:underline"
                    >
                      {quote.title}
                    </button>
                    {subtitle && <p className="mt-0.5 truncate text-xs text-muted-foreground">{subtitle}</p>}
                  </td>
                  <td className="px-3 py-3 text-sm text-muted-foreground" title={dealLabel || undefined}>
                    {dealUrl && dealLabel ? (
                      <a
                        href={dealUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        onClick={(e) => e.stopPropagation()}
                        aria-label={`Open HubSpot deal ${dealLabel}`}
                        className="block truncate text-[#0091ae] hover:underline"
                      >
                        {dealLabel}
                      </a>
                    ) : (
                      <span className="block truncate">{dealLabel || '—'}</span>
                    )}
                  </td>
                  <td className="px-3 py-3 text-sm" title={quote.companyName || undefined}>
                    <span className="block truncate">{quote.companyName || '—'}</span>
                  </td>
                  <td className="px-3 py-3" title={ownerLabel || undefined}>
                    {ownerLabel ? (
                      <span className="flex min-w-0 items-center gap-2">
                        <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-secondary text-[10px] font-bold text-primary">
                          {ownerInitials(quote)}
                        </span>
                        <span className="truncate text-xs">{ownerLabel}</span>
                      </span>
                    ) : (
                      <span className="text-sm text-muted-foreground">—</span>
                    )}
                  </td>
                  <td className="px-3 py-3">
                    <div className="flex flex-col items-start gap-1">
                      <QuoteStatusBadge quote={quote} />
                      <QuoteSignalBadges quote={quote} />
                    </div>
                  </td>
                  <td className="px-3 py-3">
                    {showSignatureStatus ? (
                      <QuoteSignatureStatus quote={quote} />
                    ) : (
                      <span className="text-sm text-muted-foreground">—</span>
                    )}
                  </td>
                  <td
                    className="px-3 py-3 text-right text-sm font-semibold tabular-nums"
                    title={quote.valueCents ? 'Totaal van de productregels' : 'Nog geen investering ingevuld'}
                  >
                    {quoteValueLabel(quote)}
                  </td>
                  {signedAt ? (
                    <td
                      colSpan={2}
                      className="whitespace-nowrap px-3 py-3 text-xs text-muted-foreground"
                      title={`Getekend op ${new Date(signedAt).toLocaleString('nl-NL', { dateStyle: 'long', timeStyle: 'short' })}`}
                    >
                      {allSigned ? formatSignedDate(signedAt) : `Getekend ${formatSignedDate(signedAt)}`}
                    </td>
                  ) : (
                    <>
                      <td className="whitespace-nowrap px-3 py-3 text-xs">
                        <span className={expiry.urgent ? 'font-semibold text-[#b8600b]' : 'text-muted-foreground'}>
                          {expiry.label}
                        </span>
                      </td>
                      <td
                        className="whitespace-nowrap px-3 py-3 text-xs text-muted-foreground"
                        title={`Aangemaakt: ${new Date(quote.createdAt).toLocaleDateString('nl-NL')}`}
                      >
                        {relativeDays(sortColumn === 'created' ? quote.createdAt : quote.updatedAt, now)}
                      </td>
                    </>
                  )}
                  <td className="py-3 pl-3 pr-4">
                    <div
                      className="flex items-center justify-end gap-1"
                      onClick={(e) => e.stopPropagation()}
                    >
                      <Button
                        variant="outline"
                        size="icon"
                        className="h-8 w-8"
                        onClick={actions.onEdit}
                        aria-label="Offerte bewerken"
                        title="Bewerken"
                      >
                        <Pencil className="h-4 w-4" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-8 w-8 text-muted-foreground"
                        onClick={actions.onView}
                        disabled={!canViewQuote(quote)}
                        aria-label="Offerte bekijken"
                        title={getViewButtonTitle(quote)}
                      >
                        <Eye className="h-4 w-4" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-8 w-8 text-muted-foreground"
                        onClick={actions.onDownloadPdf}
                        disabled={Boolean(busy.pdfDownloadBusy)}
                        aria-label="Offerte downloaden als PDF"
                        title={isPdfDownloading ? 'PDF genereren…' : 'Downloaden als PDF'}
                      >
                        {isPdfDownloading ? (
                          <Loader2 className="h-4 w-4 animate-spin" />
                        ) : (
                          <Download className="h-4 w-4" />
                        )}
                      </Button>
                      <QuoteRowActions quote={quote} actions={actions} busy={busy} />
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      <QuoteTotalsFooter
        shownCount={quotes.length}
        totalCount={totalCount}
        totalCents={totalCents}
        totalMaxCents={totalMaxCents}
        className="border-t bg-muted/30"
      />
    </div>
  );
}

export default QuoteTable;
